import React, { useEffect, useState } from "react";
import "../App.css";
import { Alert } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  buildPhoneNumber,
  DEFAULT_PHONE_COUNTRY,
  PHONE_COUNTRIES,
} from "../utils/phoneCountries";

const signupCopy = {
  hindi: {
    languageToggleLabel: 'भाषा',
    title: 'खाता बनाएं',
    subtitle: 'आज ही जापान की यात्रा शुरू करें',
    fullName: 'पूरा नाम',
    fullNamePlaceholder: 'अपना पूरा नाम दर्ज करें',
    phoneNumber: 'फोन नंबर',
    phoneNumberPlaceholder: 'अपना फोन नंबर दर्ज करें',
    birthDate: 'जन्मतिथि',
    emailOptional: 'ईमेल पता (वैकल्पिक)',
    emailPlaceholder: 'अपना ईमेल दर्ज करें',
    password: 'पासवर्ड',
    passwordPlaceholder: 'पासवर्ड बनाएं',
    confirmPassword: 'पासवर्ड की पुष्टि करें',
    confirmPasswordPlaceholder: 'अपना पासवर्ड फिर से दर्ज करें',
    passwordHint: 'कम से कम 4 अक्षर होने चाहिए',
    termsPrefix: 'मैं',
    termsLink: 'सेवा की शर्तें',
    andText: 'और',
    privacyLink: 'गोपनीयता नीति',
    termsSuffix: 'से सहमत हूँ',
    createAccount: 'खाता बनाएं',
    creating: 'बनाया जा रहा है...',
    signInPrompt: 'क्या आपके पास पहले से खाता है?',
    signIn: 'साइन इन करें',
    pleaseEnterName: 'कृपया अपना पूरा नाम दर्ज करें',
    pleaseEnterPhone: 'कृपया अपना फोन नंबर दर्ज करें',
    pleaseEnterValidEmail: 'कृपया एक मान्य ईमेल पता दर्ज करें',
    pleaseSelectBirthDate: 'कृपया अपनी जन्मतिथि चुनें',
    pleaseEnterValidPhone: 'कृपया एक मान्य फोन नंबर दर्ज करें',
    passwordLength: 'पासवर्ड कम से कम 4 अक्षर का होना चाहिए',
    passwordMismatch: 'पासवर्ड और कन्फर्म पासवर्ड मेल नहीं खाते',
    agreeTerms: 'कृपया सेवा की शर्तों और गोपनीयता नीति से सहमत हों',
    signupFailed: 'साइनअप विफल हुआ। कृपया पुनः प्रयास करें।',
    signupSuccessButTokenMissing: 'साइनअप सफल हुआ, लेकिन रिस्पॉन्स में टोकन नहीं मिला।',
    unexpectedError: 'एक अप्रत्याशित त्रुटि हुई।',
    showPassword: 'पासवर्ड दिखाएं',
    hidePassword: 'पासवर्ड छुपाएं',
    showConfirmPassword: 'कन्फर्म पासवर्ड दिखाएं',
    hideConfirmPassword: 'कन्फर्म पासवर्ड छुपाएं',
  },
  english: {
    languageToggleLabel: 'Language',
    title: 'Create Account',
    subtitle: 'Start your journey to Japan today',
    fullName: 'Full Name',
    fullNamePlaceholder: 'Enter your full name',
    phoneNumber: 'Phone Number',
    phoneNumberPlaceholder: 'Enter your phone number',
    birthDate: 'Birthdate',
    emailOptional: 'Email Address (Optional)',
    emailPlaceholder: 'Enter your email',
    password: 'Password',
    passwordPlaceholder: 'Create a password',
    confirmPassword: 'Confirm Password',
    confirmPasswordPlaceholder: 'Confirm your password',
    passwordHint: 'Must be at least 4 characters',
    termsPrefix: 'I agree to the',
    termsLink: 'Terms of Service',
    andText: 'and',
    privacyLink: 'Privacy Policy',
    termsSuffix: '',
    createAccount: 'Create Account',
    creating: 'Creating...',
    signInPrompt: 'Already have an account?',
    signIn: 'Sign In',
    pleaseEnterName: 'Please enter your full name',
    pleaseEnterPhone: 'Please enter your phone number',
    pleaseEnterValidEmail: 'Please enter a valid email address',
    pleaseSelectBirthDate: 'Please select your birth date',
    pleaseEnterValidPhone: 'Please enter a valid phone number',
    passwordLength: 'Password must be at least 4 characters',
    passwordMismatch: 'Password and confirm password do not match',
    agreeTerms: 'Please agree to Terms of Service and Privacy Policy',
    signupFailed: 'Signup failed. Please try again.',
    signupSuccessButTokenMissing: 'Signup successful but token missing in response.',
    unexpectedError: 'An unexpected error occurred.',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    showConfirmPassword: 'Show confirm password',
    hideConfirmPassword: 'Hide confirm password',
  },
};

function Signup() {
  const navigate = useNavigate();
  const [language, setLanguage] = useState(() => localStorage.getItem("signupLanguage") || "hindi");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneCountryCode, setPhoneCountryCode] = useState(DEFAULT_PHONE_COUNTRY);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const text = signupCopy[language] || signupCopy.hindi;

  useEffect(() => {
    localStorage.setItem("signupLanguage", language);
  }, [language]);

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  const sanitizePhoneInput = (value = "") => value.replace(/\D/g, "");

  const handleSignup = async () => {
    setErrorMessage("");

    if (!name.trim()) {
      setErrorMessage(text.pleaseEnterName);
      return;
    }

    if (!phoneNumber.trim()) {
      setErrorMessage(text.pleaseEnterPhone);
      return;
    }

    if (email.trim() && !isValidEmail(email.trim())) {
      setErrorMessage(text.pleaseEnterValidEmail);
      return;
    }

    if (!birthDate.trim()) {
      setErrorMessage(text.pleaseSelectBirthDate);
      return;
    }

    const formattedPhoneNumber = buildPhoneNumber(phoneNumber, phoneCountryCode);
    if (!formattedPhoneNumber || !/^\+\d{7,15}$/.test(formattedPhoneNumber)) {
      setErrorMessage(text.pleaseEnterValidPhone);
      return;
    }

    if (password.length < 4) {
      setErrorMessage(text.passwordLength);
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage(text.passwordMismatch);
      return;
    }

    if (!agreeTerms) {
      setErrorMessage(text.agreeTerms);
      return;
    }

    setLoading(true);

    try {
      const payload = {
        name: name.trim(),
        email: email.trim() || undefined,
        phone_number: formattedPhoneNumber,
        password,
        birthdate: birthDate || undefined,
        role_id: 1,
        isActive: true,
      };

      const response = await fetch("https://api.japaking.online/api/users", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || text.signupFailed);
      }

      const token = data?.message?.token;
      if (!token) {
        throw new Error(text.signupSuccessButTokenMissing);
      }

      localStorage.setItem("AuthToken", token);
      navigate("/current-status");
    } catch (error) {
      setErrorMessage(error?.message || text.unexpectedError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-v2-page">
      <div className="signup-v2-card">
        <div className="login-v2-language-toggle" role="tablist" aria-label={text.languageToggleLabel}>
          <button
            type="button"
            className={`login-v2-language-option ${language === 'hindi' ? 'active' : ''}`}
            onClick={() => setLanguage('hindi')}
          >
            हिंदी
          </button>
          <button
            type="button"
            className={`login-v2-language-option ${language === 'english' ? 'active' : ''}`}
            onClick={() => setLanguage('english')}
          >
            English
          </button>
        </div>

        <h1 className="signup-v2-title">{text.title}</h1>
        <p className="signup-v2-subtitle">{text.subtitle}</p>

        <div className="signup-v2-grid">
          <div className="signup-v2-field-block">
            <label className="signup-v2-label">
              {text.fullName} <span className="signup-v2-required-star">*</span>
            </label>
            <input
              type="text"
              className="signup-v2-input"
              placeholder={text.fullNamePlaceholder}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="signup-v2-field-block">
            <label className="signup-v2-label">
              {text.phoneNumber} <span className="signup-v2-required-star">*</span>
            </label>
            <div className="phone-country-row signup-phone-country-row">
              <div className="phone-country-select-wrap signup-phone-country-select-wrap">
                <select
                  className="phone-country-select"
                  value={phoneCountryCode}
                  onChange={(e) => setPhoneCountryCode(e.target.value)}
                >
                  {PHONE_COUNTRIES.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="phone-country-input-wrap signup-phone-country-input-wrap">
                <input
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="signup-v2-input phone-country-input"
                  placeholder={text.phoneNumberPlaceholder}
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(sanitizePhoneInput(e.target.value))}
                />
              </div>
            </div>
          </div>

          <div className="signup-v2-field-block">
            <label className="signup-v2-label">
              {text.birthDate} <span className="signup-v2-required-star">*</span>
            </label>
            <input
              type="date"
              className="signup-v2-input"
              value={birthDate}
              onChange={(e) => setBirthDate(e.target.value)}
            />
          </div>

          <div className="signup-v2-field-block">
            <label className="signup-v2-label">{text.emailOptional}</label>
            <input
              type="email"
              className="signup-v2-input"
              placeholder={text.emailPlaceholder}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="signup-v2-field-block">
            <label className="signup-v2-label">
              {text.password} <span className="signup-v2-required-star">*</span>
            </label>
            <div className="signup-v2-password-wrap">
              <input
                type={showPassword ? "text" : "password"}
                className="signup-v2-input signup-v2-password-input"
                placeholder={text.passwordPlaceholder}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="off"
              />
              <button
                type="button"
                className="signup-v2-visibility"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? text.hidePassword : text.showPassword}
              >
                {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
              </button>
            </div>
            <p className="signup-v2-password-hint">{text.passwordHint}</p>
          </div>

          <div className="signup-v2-field-block">
            <label className="signup-v2-label">
              {text.confirmPassword} <span className="signup-v2-required-star">*</span>
            </label>
            <div className="signup-v2-password-wrap">
              <input
                type={showConfirmPassword ? "text" : "password"}
                className="signup-v2-input signup-v2-password-input"
                placeholder={text.confirmPasswordPlaceholder}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="off"
              />
              <button
                type="button"
                className="signup-v2-visibility"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                aria-label={showConfirmPassword ? text.hideConfirmPassword : text.showConfirmPassword}
              >
                {showConfirmPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
              </button>
            </div>
          </div>
        </div>

        <div className="signup-v2-terms">
          <input
            type="checkbox"
            id="terms"
            checked={agreeTerms}
            onChange={(e) => setAgreeTerms(e.target.checked)}
          />
          <label htmlFor="terms">
            {text.termsPrefix}{" "}
            <a href="https://www.erishsolutions.com/terms-and-conditions" target="_blank" rel="noreferrer">
              {text.termsLink}
            </a>{" "}
            {text.andText}{" "}
            <a href="https://www.erishsolutions.com/privacy-policy" target="_blank" rel="noreferrer">
              {text.privacyLink}
            </a>
            {text.termsSuffix}
          </label>
        </div>

        {errorMessage && (
          <Alert variant="danger" className="mt-2 mb-2">
            {errorMessage}
          </Alert>
        )}

        <button className="signup-v2-button" onClick={handleSignup} disabled={loading || !agreeTerms}>
          {loading ? text.creating : text.createAccount}
        </button>

        <p className="signup-v2-signin-text">
          {text.signInPrompt}{" "}
          <Link to="/login" className="signup-v2-signin-link">{text.signIn}</Link>
        </p>
      </div>
    </div>
  );
}

export default Signup;
